// Stuck and done, talked through in chat (phone v2 + desktop v3, decided 11-03; CONTRACT 0.2.0). One home for the
// copy and the help builder, so the server, the no-server mock and both UIs say the same thing:
// - the buddy's stuck lines (F_Chat `SL` / `SLG`, D_Desk `SL` / `CALL`): a call card ("Call the dentist") gets
//   "find the number" / "write the two lines you'll say" help, every other card a first step / rough version;
// - what the app writes as the user's line when they tap (STUCK_SAID), the help card's header and button (STUCK_CARD);
// - the outcome line under a help once acted on, and the 'done' note for a card finished from chat (CHAT_NOTE).
// The six reason labels are STUCK_REASONS / STUCK_REASON_LABEL in lines.ts. Lines that did not change stay in
// LINES[ch].stuck (big with split cards, the writing card's start, energy, time).
// Tokens: {title} card title, {Task} its short name, {n} buddy name, {k} a number, {when} 'after lunch', {step}.
import type { CharId, DoneNoteData, LaterWhen, ProposedCard, StuckHelp, StuckReason } from '../types';
import { taskShort } from '../rules';
import { C } from './characters';
import { LATER_CHIPS, LINES, fill } from './lines';

export interface StuckChatLines {
  ask: string;        // after "I'm stuck on …": what's in the way? (the six reasons follow)
  big: string;        // too big, and the help is one first step (no split cards)
  start: string;      // don't know where to start, any card
  distract: string;   // keep getting distracted (no "phone face down": the help is on the phone)
  worry: string;      // worried it won't be good: a rough version
  callStart: string;  // a call card: find the number
  callWorry: string;  // a call card: write the two lines first
}

export const STUCK_CHAT: Record<CharId, StuckChatLines> = {
  sunny: {
    ask: "Fair. Life has scheduling conflicts. What's in the way?",
    big: 'Then we make it small. Like, embarrassingly small.',
    start: "Start here. That's the whole job.",
    distract: "Everything else closed. I'll guard it with my eyes.",
    worry: 'Make it bad on purpose. Bad is my whole career. It works.',
    callStart: "Start here: find the number. That's the whole job.",
    callWorry: 'Script it. Two lines, bad on purpose. Works for me every night.',
  },
  margo: {
    ask: "That's fine. What's in the way right now?",
    big: 'Then we split it. First step only.',
    start: 'Start here. Just this one step.',
    distract: "Other tabs closed. I'll stay right here.",
    worry: 'Write a rough version. Fixing comes later.',
    callStart: "Start here: find the number. That's it.",
    callWorry: 'Then we script it. Two lines, then dial. Fixing comes later.',
  },
  arjun: {
    ask: 'That happens. What makes it hard right now?',
    big: "Let's make it smaller. Just the first piece.",
    start: 'Start with just this one small step.',
    distract: "Close what you don't need. I'll stay with you.",
    worry: 'Do a rough version first. We can clean it up later.',
    callStart: 'Start with just this: find the number.',
    callWorry: "Write the two lines you'll say first. We can clean them up later.",
  },
  tavo: {
    ask: "No problem! What's in the way right now?",
    big: 'Then we break it down! First step only.',
    start: "Here's your first step. Just this one!",
    distract: "Eyes on this one! I'm right here with you.",
    worry: 'Go for a rough version! Rough still counts.',
    callStart: "Here's your first play: find the number!",
    callWorry: 'Script it! Two lines, then dial. Rough still counts.',
  },
};

/** The user's lines the server writes when they tap: Stuck on a card, a reason, "Something else". */
export const STUCK_SAID = { stuck: "I'm stuck on “{title}”.", reason: '{label}.', again: 'Something else.' };

/** The help card under her help line (F_Chat / D_Desk): header and button per reason ('time' shows the chips). */
export const STUCK_CARD: Record<StuckReason, { head: string; btn: string }> = {
  big: { head: 'FIRST STEP · 2 MIN', btn: 'Start the first step' },
  start: { head: 'FIRST STEP · 2 MIN', btn: 'Start this' },
  energy: { head: 'TWO MINUTES', btn: 'Start 2 minutes' },
  distract: { head: 'TOGETHER', btn: 'Start together' },
  worry: { head: 'ROUGH VERSION · 2 MIN', btn: 'Start a rough version' },
  time: { head: 'WHEN IS BETTER?', btn: '' },
};

/** Outcome lines (StuckChatData.out) and the 'done' note (DoneNoteData, the message text). */
export const CHAT_NOTE = {
  later: 'Moved “{Task}” to {when}',
  start: 'Working on it with {n}: {step}',
  split: 'Split “{Task}” into {k} small cards',
  tick: 'You ticked off “{Task}” · +{k} points',
  work: 'You finished “{Task}” with {n} · +{k} points',
};
/** "Moved … to {when}". */
export const LATER_SAID: Record<LaterWhen, string> = { 'In 1 hour': 'an hour from now', 'After lunch': 'after lunch', Tonight: 'tonight', Tomorrow: 'tomorrow' };

/** "Call the dentist", "Phone the bank", "Ring mom": the help is about the number and what to say. */
export const isCallTitle = (title: string) => /^(call|phone|ring)\s+\S/i.test(String(title || '').trim());
/** Writing cards ("Start here: reread your last paragraph" fits them). */
export const isWritingTitle = (title: string) => /\b(report|write|writing|essay|doc|draft|paper|letter|thesis|article|post|chapter)\b/i.test(String(title || ''));

/** Canned first step (fake AI, the mock, and the model's fallback). `why`: 'big' = too big, so one small first step;
 *  'start' = they don't know where to start. */
export function firstStepCanned(title: string, why: 'big' | 'start' = 'start'): string {
  const t = String(title || '').toLowerCase();
  if (isCallTitle(title)) return why === 'big' ? `Find the number for ${taskShort(title).replace(/^(call|phone|ring)\s+/i, '')}` : 'Find the number and open the phone app';
  if (/report|essay|doc|draft|paper|thesis|article|chapter/.test(t)) return 'Read your last paragraph';
  if (/write|letter|post/.test(t)) return 'Write one rough sentence';
  if (/call|phone|ring/.test(t)) return 'Find the number and open the phone app';
  if (/email|e-mail|mail|inbox|reply/.test(t)) return 'Open the email and read it once';
  if (/laundry|wash/.test(t)) return 'Carry the basket to the machine';
  if (/walk|run|gym|workout|exercise|stretch/.test(t)) return 'Put your shoes on';
  if (/clean|tidy|dishes|kitchen|room/.test(t)) return 'Clear one small spot';
  if (/study|read|learn|homework|review/.test(t)) return 'Open the page you stopped on';
  if (/bill|pay|tax|form/.test(t)) return 'Open the bill and find the amount';
  return why === 'big' ? 'Just the first two minutes of it' : 'Just get it open';
}

/**
 * The help for one reason (the server's engine.stuck, the mock): her line and the step to offer. `ai` = what the
 * model or the canned brain gave: split cards for a big card (15+ minutes), or a first step ('big' / 'start').
 */
export function stuckHelpFor(ch: CharId, title: string, reason: StuckReason, ai: { split?: ProposedCard[] | null; firstStep?: string | null } = {}): StuckHelp {
  const L = LINES[ch].stuck, S = STUCK_CHAT[ch];
  const call = isCallTitle(title), short = taskShort(title);
  switch (reason) {
    case 'big':
      if (ai.split && ai.split.length >= 2) return { reason, line: L.big, split: ai.split, firstStep: null };
      return { reason, line: S.big, split: null, firstStep: ai.firstStep || firstStepCanned(title, 'big') };
    case 'start': {
      const step = ai.firstStep || firstStepCanned(title, 'start');
      // A line that names the step only when the step is that one (the model writes its own steps).
      const line = step === 'Read your last paragraph' && isWritingTitle(title) ? L.start : call && /\bnumber\b/i.test(step) ? S.callStart : S.start;
      return { reason, line, firstStep: step };
    }
    case 'energy': return { reason, line: L.energy, alt: `Two minutes: ${short}` };
    case 'distract': return { reason, line: S.distract };
    case 'worry': return call ? { reason, line: S.callWorry, alt: "Write the two lines you'll say, then dial" } : { reason, line: S.worry, alt: `Rough version: ${short}` };
    default: return { reason: 'time', line: L.time, chips: [...LATER_CHIPS] };
  }
}

/** The step on the help card (its title), the thing "Start" works on. Null for split cards and the time chips. */
export function stuckStep(help: StuckHelp, title: string): string | null {
  switch (help.reason) {
    case 'big': return help.split && help.split.length ? null : help.firstStep;
    case 'start': return help.firstStep;
    case 'energy': case 'worry': return help.alt;
    case 'distract': return taskShort(title);
    default: return null;
  }
}
/** What Working together shows instead of the card's title after "Start" on a help (Card.alt); null = the title. */
export const stuckAlt = (help: StuckHelp, title: string): string | null => (help.reason === 'distract' || help.reason === 'time' ? null : stuckStep(help, title));

export const stuckOutLater = (title: string, when: LaterWhen) => fill(CHAT_NOTE.later, { Task: taskShort(title), when: LATER_SAID[when] });
export const stuckOutStart = (ch: CharId, help: StuckHelp, title: string) => fill(CHAT_NOTE.start, { n: C[ch].name, step: stuckStep(help, title) || taskShort(title) });
export const stuckOutSplit = (title: string, k: number) => fill(CHAT_NOTE.split, { Task: taskShort(title), k });
/** The 'done' note's text: "You ticked off “Call the dentist” · +15 points". */
export const doneNoteText = (ch: CharId, d: Pick<DoneNoteData, 'title' | 'gained' | 'how'>) =>
  fill(d.how === 'work' ? CHAT_NOTE.work : CHAT_NOTE.tick, { Task: taskShort(d.title), n: C[ch].name, k: d.gained });
